<script setup lang="ts">
/**
 * Lignes du panier boutique (produit × variante × quantité) —
 * modifiables/supprimables. Utilisé au step Panier et réutilisé (lecture
 * seule) dans les récaps.
 */
import type { ShopCartLineView, ShopProduct, ShopProductVariant } from "~/types/shop";

// Montants et dates dans la langue de la page.
const { formatCurrency } = useFormatLangue();

const props = defineProps<{
  cartLines: ShopCartLineView[];
  /** false = lecture seule (récap). */
  editable: boolean;
  /** Plafond courant d'une ligne (stock + max_per_order), hors la ligne. */
  getMaxForLine?: (
    product: ShopProduct,
    variant: ShopProductVariant | null,
    excludeUid?: string,
  ) => number;
}>();

defineEmits<{
  (e: "update-quantity", uid: string, quantity: number): void;
  (e: "remove-line", uid: string): void;
}>();

const config = useRuntimeConfig();
const assetsBaseUrl = computed(
  () => (config.public.apiUrl as string) || "https://api.donoor.org",
);

function thumb(line: ShopCartLineView): string | null {
  const id = line.product.images?.[0];
  if (!id) return null;
  return `${assetsBaseUrl.value}/api/assets/${id}?width=120&height=120&fit=cover&format=webp&quality=80`;
}

function lineMax(line: ShopCartLineView): number {
  if (!props.getMaxForLine) return Infinity;
  const remainingMax = props.getMaxForLine(line.product, line.variant, line.uid);
  return remainingMax;
}
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="line in cartLines"
      :key="line.uid"
      class="flex items-center gap-3 p-3 rounded-2xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f]"
    >
      <div class="w-14 h-14 rounded-xl overflow-hidden bg-gray-100 dark:bg-[#1a1a1a] shrink-0">
        <img v-if="thumb(line)" :src="thumb(line)!" :alt="line.product.name" loading="lazy" class="w-full h-full object-cover" />
        <div v-else class="w-full h-full flex items-center justify-center">
          <UIcon name="i-lucide-package" class="size-5 text-gray-300 dark:text-gray-600" />
        </div>
      </div>

      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white truncate">{{ line.product.name }}</p>
        <p v-if="line.variantLabel" class="text-xs text-[#717198] dark:text-gray-400 truncate">{{ line.variantLabel }}</p>
        <p class="text-xs text-[#717198] dark:text-gray-500 mt-0.5">
          <span v-if="line.unitPrice > 0">{{ formatCurrency(line.unitPrice) }} {{ $t("boutique.cartLines.parUnite") }}</span>
          <span v-else class="text-emerald-600 dark:text-emerald-400">{{ $t("boutique.commun.gratuit") }}</span>
        </p>
      </div>

      <div class="flex flex-col items-end gap-1.5 shrink-0">
        <span class="text-sm font-bold text-[#2d2e5c] dark:text-white tabular-nums">
          {{ line.lineTotal > 0 ? formatCurrency(line.lineTotal) : $t("boutique.commun.gratuit") }}
        </span>
        <div v-if="editable" class="flex items-center gap-1.5">
          <button
            type="button"
            :aria-label="$t('boutique.cartLines.ariaDiminuer', { nom: line.product.name })"
            class="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
            @click="$emit('update-quantity', line.uid, line.quantity - 1)"
          >
            <UIcon name="i-lucide-minus" class="size-3.5" />
          </button>
          <span class="w-5 text-center text-sm font-semibold text-[#2d2e5c] dark:text-white tabular-nums">{{ line.quantity }}</span>
          <button
            type="button"
            :aria-label="$t('boutique.cartLines.ariaAugmenter', { nom: line.product.name })"
            class="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:opacity-30 transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
            :disabled="line.quantity >= lineMax(line)"
            @click="$emit('update-quantity', line.uid, line.quantity + 1)"
          >
            <UIcon name="i-lucide-plus" class="size-3.5" />
          </button>
          <button
            type="button"
            :aria-label="$t('boutique.cartLines.ariaRetirer', { nom: line.product.name })"
            class="w-7 h-7 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-1"
            @click="$emit('remove-line', line.uid)"
          >
            <UIcon name="i-lucide-trash-2" class="size-3.5" />
          </button>
        </div>
        <span v-else class="text-xs text-[#717198] dark:text-gray-500">×{{ line.quantity }}</span>
      </div>
    </div>
  </div>
</template>
